import { useEffect, useState } from 'react';
import type { NotificationGroup, RoutingPerson } from '../../api/notificationRouting';
import { EntityModal } from '../common/EntityModal';
import { MemberChecklist } from './MemberChecklist';
import { useSaveNotificationGroup } from '../../hooks/useNotificationRouting';

export interface NotificationGroupFormModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string | null;
  /** The group being edited; absent for a new one. */
  group?: NotificationGroup | null;
  /** Who may be chosen — the office, and on an event its clients. */
  people: RoutingPerson[];
  /** Members the list no longer offers, still shown so they can be taken out. */
  extraPeople?: RoutingPerson[];
}

/** Make or change a group: its name, a note, and who is in it. */
export function NotificationGroupFormModal({
  open,
  onClose,
  eventId,
  group,
  people,
  extraPeople = [],
}: NotificationGroupFormModalProps) {
  const save = useSaveNotificationGroup();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Filled as the dialog opens, never after, so a refetch never wipes typing.
  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? '');
    setDescription(group?.description ?? '');
    setMemberIds(group?.memberIds ?? []);
    setError(null);
  }, [open, group]);

  const choosable = [
    ...people,
    ...extraPeople.filter((extra) => !people.some((p) => p.id === extra.id)),
  ];

  const submit = async () => {
    if (!name.trim()) {
      setError('Give the group a name.');
      return;
    }
    setError(null);
    try {
      await save.mutateAsync({
        id: group?.id,
        eventId,
        name: name.trim(),
        description: description.trim() || null,
        memberIds,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The group could not be saved.');
    }
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={group ? 'Edit group' : 'New group'}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-lg px-4 text-sm font-medium text-gray-700 hover:bg-gray-100 sm:min-h-9"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={save.isPending}
            className="min-h-11 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60 sm:min-h-9"
          >
            {save.isPending ? 'Saving…' : group ? 'Save' : 'Create group'}
          </button>
        </div>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div>
          <label htmlFor="group-name" className="block text-sm font-medium text-gray-700">
            Name
          </label>
          <input
            id="group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Money team"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-base sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="group-description" className="block text-sm font-medium text-gray-700">
            Note <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            id="group-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this group is for"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-base sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="group-members" className="block text-sm font-medium text-gray-700">
            Members
          </label>
          <p className="mb-1 text-xs text-gray-500">
            {eventId
              ? 'People in the office, and this event’s clients.'
              : 'People in the office — administrators and producers.'}
          </p>
          <MemberChecklist
            inputId="group-members"
            people={choosable}
            value={memberIds}
            onChange={setMemberIds}
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
      </form>
    </EntityModal>
  );
}
